import React, { useState, useEffect, useMemo } from 'react';

import _ from 'lodash';
import {
  Stack,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  Divider,
  TextField,
  Button,
  Tabs,
  Tab,
  AppBar,
  Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { timestamp } from 'src/utils/firebase';
import { useAuthContext } from 'src/auth/hooks';
import KeywordToolTip from 'src/components/keyword-tooltip';
import useScenarioService from 'src/services/scenario-service';
import { useFinanceContext } from 'src/components/plaid/datasource';
import { useBoolean } from 'src/hooks/use-boolean';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Scrollbar from 'src/components/scrollbar';
import Iconify from 'src/components/iconify';
import { PatchItemRow } from 'src/components/plaid/budget/budget-patch-dialog';
import { saveBudgetPatches } from 'src/components/plaid/budget';
import AddPatchCategoryRow from './AddPatchCategoryRow';
import { findChangedObjects } from '../../utils/findChangedObjects';
import { PercentField } from './PercentField';
import { CurrencyField } from './CurrencyField';
import ScenarioSelect from './scenario-select';
import AddScenarioDialog from './add-scenario-dialog';

export default function ConstantsEditor({ closeDrawer }) {
  const { user } = useAuthContext();
  const { scenarios, updateScenario, deleteScenario } = useScenarioService(user?.id);
  const {
    scenario,
    setScenario,
    baseAssumptions,
    scenarioAssumptions,
    systemAssumptions,
    patches: currentPatches,
  } = useFinanceContext();

  const [expand, setExpand] = useState(false);

  // maintain a state for editing patches
  const [patches, setPatches] = useState([]);
  useEffect(() => {
    setPatches(_.cloneDeep(currentPatches));
  }, [currentPatches, scenarios]);

  const [currentScenario, setCurrentScenario] = useState(null);
  useEffect(() => {
    const _currentScenario = scenarios?.find((e) => e?.id === scenario);
    setCurrentScenario(_currentScenario);
    setDescription(_currentScenario?.description ?? '');
  }, [scenario, scenarios]);

  const {
    targetRetirementAge,
    averageIncomeGrowthRate,
    taxRateRetention,
    annualInflationRate,
    averageEquityGrowthRate,
    averageRealEstateGrowthRate,
    socialSecurityIncome,
    averagePrivateAssetGrowthRate,
    averageEquityWithdrawalRate,
    averageRetirementEquityWithdrawalRate,
  } = scenarioAssumptions;

  const [constants, setConstants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAddScenario, setShowAddScenario] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [description, setDescription] = useState('');
  const confirmDelete = useBoolean(false);
  const [editData, setEditData] = useState(null);

  // Determines the fallback value while assembling the assumptions stack (scenario, base, system)
  const underlyingValueForKey = (key, curValue) => {
    const isDefault = scenario === 'default';
    let value;

    // log for debugging the assumptions stack
    const debug = (...msgs) =>
      process.env.REACT_APP_ENV === 'development' &&
      console.log(...msgs, { baseAssumptions, scenarioAssumptions, systemAssumptions });

    // scenario is the most specific layer, we prefer these values.
    // The scenario assumption can be customized by the user
    if (!value && curValue && !isDefault) {
      value = scenarioAssumptions?.[key];
      debug(`✨ ${scenario} ${key} scenario assumption`, { value, curValue });
    }

    // If there is no scenario-defined value, fallback to base-assumption.
    // The base assumption is the next most specific layer, which underpins all other scenarios and can be customized
    if (!value && !isDefault) {
      value = baseAssumptions?.[key];
      debug(`🧱 ${scenario} ${key} base assumption`, { value, curValue });
    }

    // If there is no scenario-defined or base-defined value, fallback to system-assumption.
    // The system assumption is the most generic layer, which underpins all systems and is always defined
    if (!value) {
      value = systemAssumptions?.[key];
      debug(`💻 ${scenario} ${key} system assumption`, { value, curValue });
    }

    return value;
  };

  // retrieves only the user defined value (if any) for the scenario. This is what's used inside of the text fields.
  const valueForKey = (key) =>
    constants?.flatMap((e) => e.data)?.find((e) => e.key === key)?.value ??
    scenarioAssumptions[key];

  // reset the fields after making changes and clicking discard
  function resetFields() {
    setConstants([
      {
        category: 'Financial Information',
        data: [
          {
            value: averageIncomeGrowthRate,
            placeholder: underlyingValueForKey('averageIncomeGrowthRate'),
            label: 'Average Annual Income Growth',
            key: 'averageIncomeGrowthRate',
            scaler: 100,
            valueOffset: 1,
            yearly: true,
            type: 'percent',
            stepSize: 0.05,
          },
          {
            value: averagePrivateAssetGrowthRate,
            placeholder: underlyingValueForKey('averagePrivateAssetGrowthRate'),
            label: 'Average Private Asset Growth Rate',
            key: 'averagePrivateAssetGrowthRate',
            scaler: 100,
            valueOffset: 1,
            stepSize: 0.05,
            type: 'percent',
            yearly: true,
          },
          // Add other financial constants here
        ],
      },
      {
        category: 'Tax Information',
        data: [
          {
            value: taxRateRetention,
            placeholder: underlyingValueForKey('taxRateRetention'),
            label: 'Tax Retention Rate',
            key: 'taxRateRetention',
            yearly: true,
            type: 'percent',
            scaler: 100,
            stepSize: 1,
          },
          // Add other tax constants here
        ],
      },
      {
        category: 'Inflation and Growth Rates',
        data: [
          {
            value: annualInflationRate,
            placeholder: underlyingValueForKey('annualInflationRate'),
            label: 'Annual Inflation Rate',
            key: 'annualInflationRate',
            type: 'percent',
            scaler: 100,
            yearly: true,
            valueOffset: 1,
            stepSize: 0.05,
          },
          {
            value: averageRealEstateGrowthRate,
            placeholder: underlyingValueForKey('averageRealEstateGrowthRate'),
            label: 'Average Annual Real Estate Growth Rate',
            key: 'averageRealEstateGrowthRate',
            type: 'percent',
            yearly: true,
            scaler: 100,
            valueOffset: 1,
            stepSize: 0.05,
          },
          {
            value: averageEquityGrowthRate,
            placeholder: underlyingValueForKey('averageEquityGrowthRate'),
            label: 'Average Annual Financial Market Portfolio Growth Rate',
            key: 'averageEquityGrowthRate',
            type: 'percent',
            yearly: true,
            scaler: 100,
            valueOffset: 1,
            stepSize: 0.05,
          },
          {
            value: averageEquityWithdrawalRate,
            placeholder: underlyingValueForKey('averageEquityWithdrawalRate'),
            label: `Average Annual Portfolio Dividend Rate`,
            keyword: 'equity withdrawal',
            key: 'averageEquityWithdrawalRate',
            type: 'percent',
            yearly: true,
            scaler: 100,
            stepSize: 0.05,
          },
          // Add other inflation and growth rate constants here
        ],
      },
      {
        category: 'Qualified Retirement Income',
        data: [
          {
            value: targetRetirementAge,
            placeholder: underlyingValueForKey('targetRetirementAge'),
            label: 'Target Retirement Age',
            key: 'targetRetirementAge',
            stepSize: 1,
            type: 'number',
          },
          {
            value: socialSecurityIncome,
            placeholder: underlyingValueForKey('socialSecurityIncome'),
            label: `Social Security Income (at ${
              targetRetirementAge ??
              valueForKey('targetRetirementAge') ??
              underlyingValueForKey('targetRetirementAge')
            })`,
            key: 'socialSecurityIncome',
            type: 'currency',
            monthly: true,
            stepSize: 100,
          },
          {
            value: averageRetirementEquityWithdrawalRate,
            placeholder: underlyingValueForKey('averageRetirementEquityWithdrawalRate'),
            label: `IRA/401k Monthly Withdrawal Rate (at ${
              targetRetirementAge ??
              valueForKey('targetRetirementAge') ??
              underlyingValueForKey('targetRetirementAge')
            })`,
            key: 'averageRetirementEquityWithdrawalRate',
            type: 'percent',
            yearly: true,
            scaler: 100,
          },

          // Add other retirement income constants here
        ],
      },
    ]);
  }

  // Update the field values when the scenario changes
  useEffect(() => {
    resetFields();
    if (scenario === 'default') {
      setSelectedTab(0);
    }
  }, [scenarioAssumptions, scenario]);

  const [changedAssumptions, isChanged] = useMemo(() => {
    const changed_assumptions = constants
      .flatMap((e) => e.data)
      ?.filter((e) => {
        const currentValue = scenarioAssumptions?.[e.key];
        return (
          (e.value !== currentValue && e.value !== undefined && e.value !== null) ||
          (currentValue && (e.value === undefined || e.value === null))
        );
      });

    // Compare the arrays and find changed objects
    const budgets_changed = findChangedObjects(currentPatches, patches);

    const is_changed =
      changed_assumptions?.length > 0 ||
      (scenario !== 'default' && budgets_changed?.length) ||
      (description && description !== scenarios?.find((e) => e.id === scenario)?.description);

    return [changed_assumptions, is_changed];
  }, [patches, constants, description]);

  const handleChange = (categoryKey, constantKey, v) => {
    setConstants((prev) =>
      prev.map((category) => {
        if (category.category === categoryKey) {
          category.data = category.data.map((constant) => {
            if (constant.label === constantKey) {
              constant.value = v === '' || v === null || v === undefined ? null : v;
            }
            return constant;
          });
        }
        return category;
      })
    );
  };

  // write the new key assumptions to the db
  const saveChanges = async ({ reset = false }) => {
    // format the key assumptions
    const constantsObject = changedAssumptions.reduce((acc, constant) => {
      if (constant.value === null || constant.value === '') {
        // Explicitly set null for cleared fields
        acc[constant.key] = null;
      } else if (constant.type === 'date') {
        acc[constant.key] = timestamp(constant.value);
      } else if (constant.type === 'currency' && constant.value) {
        acc[constant.key] = Number(constant.value);
      } else if (constant.type === 'percent') {
        acc[constant.key] = constant.value;
      } else {
        acc[constant.key] = reset ? null : Number(valueForKey(constant.key));
      }
      return acc;
    }, {});

    try {
      setLoading(true);
      // update the scenario
      await updateScenario(scenario, {
        ...constantsObject,
        description,
        name: currentScenario?.name,
      });
      await saveBudgetPatches(patches);
      closeDrawer?.();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Remove the new key assumptions from the db
  const completeDelete = async () => {
    console.log('deleting scenario', scenario);
    setDeleting(true);
    await deleteScenario(scenario);

    // set a new active scenario
    setScenario(scenarios?.[0]?.id ?? 'default');

    // close the dialog
    confirmDelete.onFalse();
    setDeleting(false);
    closeDrawer?.();
  };

  // Close the modal
  const handleClose = async (selectedScenario) => {
    if (selectedScenario) {
      resetFields();
      setScenario(selectedScenario?.id ?? 'default');
    }
    await setShowAddScenario(false);
  };

  const size = 12;

  // select the text in the field upon click, easier to enter a new value
  const handleFocus = (event) => event.target.select();

  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const contentView = useMemo(
    () =>
      constants.map(({ category, data }) => (
        <Accordion
          key={category}
          expanded={expandedAccordion === category}
          onChange={handleAccordionChange(category)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{category}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container justifyContent="space-evenly" spacing={2}>
              {data.map(
                ({
                  value,
                  label,
                  keyword,
                  scaler = 1,
                  valueOffset = 0,
                  type,
                  monthly,
                  yearly,
                  stepSize = 0.005,
                  placeholder,
                }) => (
                  <Grid item key={label} xs={size}>
                    {type === 'date' ? (
                      <FormControl fullWidth key={label}>
                        <DatePicker
                          onFocus={handleFocus}
                          label={<KeywordToolTip keyword={keyword ?? label} />}
                          value={value ?? new Date()}
                          onChange={(e) => handleChange(category, label, e)}
                          fullWidth
                        />
                      </FormControl>
                    ) : type === 'currency' ? (
                      <CurrencyField
                        key={label}
                        label={label}
                        handleFocus={handleFocus}
                        placeholder={placeholder ?? label}
                        value={value}
                        stepSize={stepSize}
                        monthly={monthly ? String(monthly) : null}
                        yearly={yearly ? String(yearly) : null}
                        onChange={(e) => handleChange(category, label, e.target.value)}
                      />
                    ) : type === 'percent' ? (
                      <PercentField
                        key={label}
                        fullWidth
                        value={value}
                        scaler={scaler}
                        type={type}
                        valueOffset={valueOffset}
                        label={label}
                        handleFocus={handleFocus}
                        placeholder={placeholder ?? label}
                        stepSize={stepSize}
                        monthly={monthly}
                        yearly={yearly}
                        onChange={(e) => handleChange(category, label, e.target.value)}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        type="number"
                        value={value ?? ''}
                        scaler={scaler}
                        label={label}
                        handleFocus={handleFocus}
                        placeholder={placeholder ?? label}
                        onChange={(e) => handleChange(category, label, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </Grid>
                )
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )),
    [constants, expandedAccordion]
  );

  // Render the key assumptions constants inputs in an accordion

  const isDefaultScenario = (patch) => _.isEmpty(patch?.scenario);
  const isScenario = (patch) => !isDefaultScenario(patch);

  const orderedPatches = () => {
    const results = [...(patches ?? []).filter((patch) => patch.scenario === scenario)];
    results.sort((a, b) => {
      const aDisabled = isScenario(a);
      const bDisabled = isScenario(b);
      // Compare timestamps
      const timestampDiff =
        (a.timestamp?.toDate()?.getTime() ?? 0) - (b.timestamp?.toDate()?.getTime() ?? 0);

      // If timestamps are equal, handle disabled items
      if (timestampDiff === 0) {
        // If one item is disabled and the other is not, place the disabled one after the enabled one
        if (aDisabled && !bDisabled) {
          return -1;
        }
        if (!aDisabled && bDisabled) {
          return 1;
        }
      }

      // Otherwise, maintain the sorting based on timestamps
      return timestampDiff;
    });

    results.reverse();
    return results;
  };

  const patchesForScenario = orderedPatches();

  const scenarioPatchChanged = (patchId, newData) => {
    let patchToUpdate = patches?.find((p) => p?.id === patchId);
    if (patchToUpdate) {
      patchToUpdate = {
        user_id: user.id,
        timestamp: timestamp(newData?.date),
        ...patchToUpdate,
        categories: { ...patchToUpdate?.categories, ...newData },
      };

      setPatches([...(patches?.filter((e) => e?.id !== patchId) ?? []), patchToUpdate]);
    }
  };

  const removePatchItem = (patchId, cat) => {
    // Find the index of the patch with the matching patchId in your state
    const patchIndex = patches.findIndex((patch) => patch.id === patchId);

    if (patchIndex !== -1) {
      // Clone the original patch to avoid mutating the state directly
      const updatedPatch = { ...patches[patchIndex] };

      // Remove the specified category from the patch's categories
      if (updatedPatch.categories) {
        delete updatedPatch.categories[cat];
      }

      // Create a new array with the updated patch at the same index
      const updatedPatches = [
        ...patches.slice(0, patchIndex),
        updatedPatch,
        ...patches.slice(patchIndex + 1),
      ];

      // Update the state with the new patches array
      setPatches(updatedPatches);
    }
  };

  // Render the budget changes
  const renderBudgetChanges = () => (
    <Stack spacing={1} padding={2}>
      <Alert severity="info">
        Scenario budget changes will only be applied when the current scenario is active.
      </Alert>

      <AddPatchCategoryRow {...{ expand, setExpand }} />
      <Divider />
      {patchesForScenario?.map(
        (patch, index) =>
          !_.isEmpty(patch?.categories) && (
            <PatchItemRow
              key={patch.id ?? index}
              patch={patch}
              // filterKey={row}
              initialForecast={{}}
              onChange={scenarioPatchChanged}
              onDeleteRow={removePatchItem}
              nameForAccount={() => {}}
            />
          )
      )}
    </Stack>
  );

  return (
    <>
      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title={`Delete ${currentScenario?.name ?? 'Base Case'}?`}
        content={
          <>
            Are you sure want to delete the{' '}
            <strong> {currentScenario?.name ?? 'Base Case'} </strong>
            scenario? This action cannot be undone.
          </>
        }
        action={
          <LoadingButton
            loading={deleting}
            variant="contained"
            color="error"
            onClick={completeDelete}
          >
            Delete
          </LoadingButton>
        }
      />

      <AddScenarioDialog open={showAddScenario} onClose={handleClose} editData={editData} />
      <Scrollbar>
        <Stack sx={{ px: 2, paddingTop: 2 }}>
          <ScenarioSelect
            options={scenarios}
            value={scenario ?? 'default'}
            onChange={setScenario}
            onAdd={() => {
              setShowAddScenario(true);
              setEditData(null);
            }}
            onRename={() => {
              setShowAddScenario(true);
              setEditData({
                id: scenario ?? 'default',
                name: 'Base Case',
                description: 'Default Financial Climate',
                ...scenarios?.find((e) => e?.id === scenario),
              });
            }}
          />

          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            label="Scenario Description"
            placeholder="High growth rate"
          />

          <Tabs
            centered
            value={selectedTab}
            onChange={(e, v) => setSelectedTab(v)}
            variant="fullWidth"
          >
            <Tab
              value={0}
              label="Assumptions"
              icon={<Iconify icon="material-symbols:square-foot" />}
            />
            {scenario !== 'default' && (
              <Tab
                value={1}
                label="Budget"
                icon={<Iconify icon="material-symbols:shopping-cart-rounded" />}
              />
            )}
          </Tabs>
        </Stack>
        {selectedTab === 0 && contentView}
        {selectedTab === 1 && renderBudgetChanges()}
      </Scrollbar>
      {!expand && (
        <AppBar position="sticky">
          <Stack spacing={1} sx={{ p: 2, bottom: 0, left: 0, right: 0 }}>
            <LoadingButton
              loading={loading}
              variant="contained"
              size="large"
              fullWidth
              color="success"
              disabled={!isChanged}
              onClick={saveChanges}
            >
              Save Changes
            </LoadingButton>
            <Stack direction="row">
              <Button
                variant="text"
                size="small"
                fullWidth
                disabled={!isChanged}
                onClick={handleClose}
              >
                Discard Changes
              </Button>

              <LoadingButton
                loading={deleting}
                variant="text"
                size="small"
                fullWidth
                color="error"
                onClick={confirmDelete.onTrue}
              >
                Delete Scenario
              </LoadingButton>
            </Stack>
          </Stack>
        </AppBar>
      )}
    </>
  );
}
